/**
 * Tagged visits and word-of-mouth chains, browser side (job #703; server: api/go/*, rules in
 * api/_utils/goLinks.ts).
 *
 * Two ways a tab is TAGGED:
 *   - it came through a minted link saysomethingin.app/go/<code>: the redirect hands over
 *     ?ssi_go=<code>&ssi_a=<session token> (the arrival is already recorded server-side);
 *   - it came through an in-game share: ?gs=<the sharer's token>; this page records the arrival,
 *     with that token as its parent.
 * Either way the tags are stashed in sessionStorage AT BOOT (main.js, before the router can drop the
 * query) and stripped from the address bar, so in-app browsers that lose parameters later, and a
 * copied address, carry nothing on. Later steps of a tagged tab — a game played, a course picked, a
 * sign-in — are sent once each with the session token. An untagged tab sends nothing here.
 *
 * THE SHARER TOKEN. Every share button adds ?gs=<token> to the link it shares, so the friends it
 * brings can be counted and, when they share again, the chain followed. The token is random and lives
 * in sessionStorage only — this tab, gone when it closes (Tom, 2026-10-15: nothing is kept on the
 * device for counting, so no question is asked). It is never an account, an address or a fingerprint; the server
 * learns an account only from a real sign-in token, at the sign-in step of a tagged tab.
 */
import { watch, type Ref } from 'vue'
import { countingOff, store } from '@/composables/countingConsent'

export const GO_CTX_KEY = 'ssi-go-ctx'
export const GO_TOKEN_KEY = 'ssi-go-token'
export const SHARE_PARAM = 'gs'
const ALPHABET = '23456789abcdefghjkmnpqrstuvwxyz'
export const TOKEN_RE = /^[23456789abcdefghjkmnpqrstuvwxyz]{12}$/
const CODE_RE = /^[23456789abcdefghjkmnpqrstuvwxyz]{7}$/

export type GoStep = 'played' | 'picked' | 'signed_in'

export interface GoCtx {
  /** the minted link this tab came by, if any */
  link: string | null
  /** the token of the share that brought this tab, if any */
  parent: string | null
  /** this tab's session token: given by the /go/ redirect, or made here */
  token: string
  landedAt: string
  /** steps already sent from this tab */
  sent: string[]
}

type Kv = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>
function tab(): Kv | null {
  try {
    return typeof sessionStorage !== 'undefined' ? sessionStorage : null
  } catch {
    return null
  }
}

export function newToken(rand: (n: number) => number = cryptoInt): string {
  let s = ''
  for (let i = 0; i < 12; i++) s += ALPHABET[rand(ALPHABET.length)]
  return s
}
function cryptoInt(n: number): number {
  try {
    const a = new Uint32Array(1)
    crypto.getRandomValues(a)
    return a[0] % n
  } catch {
    return Math.floor(Math.random() * n)
  }
}

export function readCtx(s: Kv | null = tab()): GoCtx | null {
  try {
    const c = JSON.parse(s?.getItem(GO_CTX_KEY) || 'null') as GoCtx | null
    return c && TOKEN_RE.test(c.token) && Array.isArray(c.sent) ? c : null
  } catch {
    return null
  }
}
function writeCtx(c: GoCtx, s: Kv | null = tab()): void {
  try {
    s?.setItem(GO_CTX_KEY, JSON.stringify(c))
  } catch {
    /* private mode: the arrival still counted, the later steps cannot */
  }
}

/** This tab's sharer token: the one it already has, else a new one for this tab. Never kept on the device. */
export function sharerToken(s: Kv | null = tab()): string {
  let t: string | null = null
  try {
    t = s?.getItem(GO_TOKEN_KEY) ?? null
  } catch {
    /* blocked reads must not prevent sharing */
  }
  if (!t || !TOKEN_RE.test(t)) {
    t = newToken()
    try {
      s?.setItem(GO_TOKEN_KEY, t)
    } catch {
      /* private mode: a token for this page only */
    }
  }
  return t
}

function isProbe(): boolean {
  try {
    if (typeof navigator !== 'undefined' && navigator.webdriver) return true
    if (typeof location !== 'undefined' && new URLSearchParams(location.search).get('probe') === '1') return true
    return store()?.getItem('ssi_probe') === '1'
  } catch {
    return false
  }
}

type Post = (url: string, init: RequestInit) => Promise<unknown>
let poster: Post = (url, init) => fetch(url, init)
/** Test seam. */
export function setGoPoster(p: Post): void {
  poster = p
}

function post(body: Record<string, unknown>, token: string | null = null): void {
  if (countingOff()) return
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) }
    void Promise.resolve(poster('/api/go/event', { method: 'POST', headers, body: JSON.stringify({ ...body, probe: isProbe() }), keepalive: true })).catch(() => null)
  } catch {
    /* counting never gets in the way */
  }
}

function ownReferrerHost(ref: string, here: string): string | null {
  try {
    const h = new URL(ref).hostname.toLowerCase().replace(/^www\./, '')
    if (!h || h === here.replace(/^www\./, '') || /(^|\.)saysomethingin\.app$/.test(h)) return null
    return /^[a-z0-9.-]{1,100}$/.test(h) ? h : null
  } catch {
    return null
  }
}

/**
 * At boot, before the router. Stashes this tab's tags, records a share arrival, and takes our
 * parameters off the address. A tab that already carries a ctx keeps it (a reload, a move inside).
 */
export function captureGoLanding(opts: { href?: string; referrer?: string; s?: Kv | null; replace?: (url: string) => void; now?: number } = {}): GoCtx | null {
  const s = opts.s === undefined ? tab() : opts.s
  let url: URL
  try {
    url = new URL(opts.href ?? location.href)
  } catch {
    return readCtx(s)
  }
  const q = url.searchParams
  const link = q.get('ssi_go')
  const given = q.get('ssi_a')
  const parent = q.get(SHARE_PARAM)
  const ours = ['ssi_go', 'ssi_a', SHARE_PARAM].filter((k) => q.has(k))
  if (ours.length) {
    for (const k of ours) q.delete(k)
    const clean = `${url.pathname}${url.search}${url.hash}`
    try {
      ;(opts.replace ?? ((u: string) => history.replaceState(history.state, '', u)))(clean)
    } catch {
      /* the address keeps them; nothing else changes */
    }
  }
  const existing = readCtx(s)
  const landedAt = new Date(opts.now ?? Date.now()).toISOString()
  if (link && CODE_RE.test(link) && given && TOKEN_RE.test(given)) {
    // A /go/ arrival: the server already counted it under this token.
    const ctx: GoCtx = { link, parent: null, token: given, landedAt, sent: ['arrived'] }
    writeCtx(ctx, s)
    try {
      s?.setItem(GO_TOKEN_KEY, given)
    } catch {
      /* private mode */
    }
    return ctx
  }
  if (parent && TOKEN_RE.test(parent)) {
    const token = sharerToken(s)
    if (parent === token) return existing // my own share opened on my own device: no arrival
    if (existing && existing.parent === parent) return existing
    const ctx: GoCtx = { link: null, parent, token, landedAt, sent: ['arrived'] }
    writeCtx(ctx, s)
    post({ step: 'arrived', token, parent, what: `${url.pathname}`, game: gameOf(url.pathname), referrerHost: ownReferrerHost(opts.referrer ?? (typeof document !== 'undefined' ? document.referrer : ''), url.hostname) })
    return ctx
  }
  return existing
}

/** The game a path belongs to, as a short name: /guess/hi/pin-it → pin-it, /words/cym → words. */
export function gameOf(path: string): string | null {
  const p = path.split(/[?#]/)[0]
  if (/^\/guess\/(?:[a-z-]+\/)?pin-it/.test(p)) return 'pin-it'
  if (/^\/guess\/(?:[a-z-]+\/)?listen/.test(p) || p.startsWith('/listen')) return 'listen'
  if (p.startsWith('/guess') || p.startsWith('/g/')) return 'guess'
  if (p.startsWith('/words')) return 'words'
  if (p.startsWith('/script') || p.includes('/script')) return 'script'
  return null
}

/** A later step of a tagged tab, once each. Nothing for an untagged tab. */
export function recordGoStep(step: GoStep, extra: { game?: string | null; authToken?: string | null } = {}, s: Kv | null = tab()): boolean {
  const ctx = readCtx(s)
  if (!ctx || ctx.sent.includes(step)) return false
  if (step === 'signed_in' && !extra.authToken) return false
  ctx.sent = [...ctx.sent, step]
  writeCtx(ctx, s)
  post(
    { step, token: ctx.token, link: ctx.link, parent: ctx.parent, game: extra.game ?? null, what: typeof location !== 'undefined' ? location.pathname : null, landedAt: ctx.landedAt },
    step === 'signed_in' ? extra.authToken : null,
  )
  return true
}

/**
 * The link a share button shares: the same link with this person's sharer token on it, and the
 * share recorded (with the token of whoever brought them, which is what makes the chain). Only our
 * own links are tagged; anything else is shared as it is.
 */
export function tagShareUrl(raw: string, game?: string | null, s: Kv | null = tab()): string {
  let u: URL
  try {
    u = new URL(raw, typeof location !== 'undefined' ? location.origin : 'https://saysomethingin.app')
  } catch {
    return raw
  }
  const here = typeof location !== 'undefined' ? location.hostname : 'saysomethingin.app'
  if (u.hostname !== here && !/(^|\.)saysomethingin\.app$/.test(u.hostname)) return raw
  const token = sharerToken(s)
  u.searchParams.set(SHARE_PARAM, token)
  const ctx = readCtx(s)
  post({ step: 'shared', token, parent: ctx?.token === token ? ctx.parent : null, link: ctx?.token === token ? ctx.link : null, game: game ?? gameOf(u.pathname), what: u.pathname })
  return u.toString()
}

/** App-level: the sign-in step of a tagged tab, whenever the tab becomes signed in. */
export function noteGoSignIn(getToken: () => Promise<string | null>): void {
  if (!readCtx()) return
  void getToken()
    .then((t) => (t ? recordGoStep('signed_in', { authToken: t }) : false))
    .catch(() => false)
}

/** App-level (in App.vue): note the sign-in whenever the tab becomes signed in. */
export function watchGoSignIn(isAuthenticated: Ref<boolean>, getToken: () => Promise<string | null>): void {
  watch(isAuthenticated, (yes) => { if (yes) noteGoSignIn(getToken) }, { immediate: true })
}

/** Our own parameters, which the router can put back on the address from its first read of it. */
export const GO_PARAMS = ['ssi_go', 'ssi_a', SHARE_PARAM] as const

/**
 * After the router's first navigation: take our parameters off the address again. The router read
 * the address before the boot capture ran (its module is imported first) and restores it whole, so
 * without this a copied address would carry this tab's token to somebody else.
 */
export function stripGoParams(router: { currentRoute: { value: { path: string; query: Record<string, unknown>; hash: string } }; replace: (to: unknown) => unknown }): void {
  const r = router.currentRoute.value
  if (!GO_PARAMS.some((k) => k in r.query)) return
  const query = { ...r.query }
  for (const k of GO_PARAMS) delete query[k]
  void Promise.resolve(router.replace({ path: r.path, query, hash: r.hash })).catch(() => null)
}
